import type { UserDropdownItemProps } from './UserDropdown.types';

/**
 * Individual menu item for the user dropdown.
 * Supports icon, label, optional keyboard shortcut, and danger styling.
 */
export function UserDropdownItem({
  icon,
  label,
  onClick,
  href,
  shortcut,
  meta,
  isDanger = false,
  className = '',
}: UserDropdownItemProps) {
  const baseStyles = `
    flex items-center gap-3
    w-full py-3 px-4
    border-none bg-transparent
    text-label-md font-medium text-left
    font-inherit no-underline
    cursor-pointer
    transition-all duration-150 ease-out
  `;

  const normalStyles = `
    text-neutral-300
    hover:bg-primary-300/[0.08] hover:text-neutral-50
    focus-visible:outline-none focus-visible:bg-primary-300/[0.08] focus-visible:text-neutral-50
    [&_svg]:text-neutral-500 [&:hover_svg]:text-neutral-400
  `;

  const dangerStyles = `
    text-error-400
    hover:bg-error-400/10 hover:text-error-400
    focus-visible:outline-none focus-visible:bg-error-400/10
    [&_svg]:text-error-400
  `;

  const combinedStyles = `
    ${baseStyles}
    ${isDanger ? dangerStyles : normalStyles}
    ${className}
  `.trim();

  const content = (
    <>
      <span className="w-[18px] h-[18px] flex-shrink-0" aria-hidden="true">
        {icon}
      </span>
      <span className="flex-1">{label}</span>
      {meta && (
        <span className="text-label-sm text-neutral-500 font-mono">{meta}</span>
      )}
      {shortcut && (
        <kbd className="hidden sm:inline text-label-sm text-neutral-500 font-mono">
          {shortcut}
        </kbd>
      )}
    </>
  );

  if (href) {
    return (
      <a href={href} className={combinedStyles} role="menuitem">
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={combinedStyles}
      role="menuitem"
      onClick={onClick}
    >
      {content}
    </button>
  );
}

export default UserDropdownItem;
